import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Info, Paperclip } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { TicketContext } from '../../contexts/TicketContext';
import { TicketDto } from '../../services/ticketApi';
import { getUserTimezone } from '../../utils/timezoneUtils';
import { options, getBotReply } from '../../utils/helperFunctions';

interface Message {
  id: number;
  sender: 'self' | 'agent';
  senderName: string;
  agentInitials?: string;
  text: string;
  time: string;
}

const statusClass: Record<string, string> = {
  'In Progress': 'hs-status-inprogress',
  'Open':        'hs-status-open',
  'Resolved':    'hs-status-resolved',
  'Closed':      'hs-status-closed',
};

const priorityColor: Record<string, string> = {
  High: '#E05252', Medium: '#E2AF75', Low: '#47C08F',
};

const TicketDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const ticketContext = useContext(TicketContext);
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timeZone = userPreferences.timezone || getUserTimezone();

  const formatDate = (utcDate: string) =>
    utcDate ? new Date(utcDate).toLocaleString('en-US', { timeZone, ...options }) : '';

  const [ticket, setTicket] = useState<TicketDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ticketContext || !id) return;
    ticketContext.getTicketById(Number(id)).then((data) => {
      if (data) {
        setTicket(data);
        // Seed conversation with the ticket description as the first message if present
        if (data.ticketDescription) {
          setMessages([{
            id: 1,
            sender: 'self',
            senderName: 'You',
            agentInitials: 'ME',
            text: data.ticketDescription,
            time: formatDate(data.ticketCreatedAt),
          }]);
        }
      }
      setLoading(false);
    });
  }, [id]);

  // Auto-scroll to bottom on new messages or typing indicator
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = () => {
    const text = newMessage.trim();
    if (!text) return;

    const now = new Date().toLocaleString('en-US', { timeZone, ...options });

    setMessages((prev) => [
      ...prev,
      { id: prev.length + 1, sender: 'self', senderName: 'You', agentInitials: 'ME', text, time: now },
    ]);
    setNewMessage('');

    // Bot typing indicator then reply
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          sender: 'agent',
          senderName: 'SmartEco Bot',
          agentInitials: 'SE',
          text: getBotReply(text),
          time: new Date().toLocaleString('en-US', { timeZone, ...options }),
        },
      ]);
    }, 1000);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="hs-detail-container zoomOutLevel" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--color-sub-title-text)' }}>Loading ticket...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!ticket) {
    return (
      <DashboardLayout>
        <div className="hs-detail-container zoomOutLevel" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--color-sub-title-text)' }}>Ticket not found.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="hs-detail-container">
        <div className='hs-detail-container-wrapper zoomOutLevel'>
          {/* Back + Title */}
          <div className="hs-detail-back">
            <button className="hs-detail-back-btn" onClick={() => navigate('/help-support')}>
              <ArrowLeft size={22} />
            </button>
            <h1 className="hs-detail-title">{ticket.ticketSubject}</h1>
          </div>

          {/* Status + Ticket ID */}
          <div className="hs-detail-meta">
            <span className={`hs-status-badge ${statusClass[ticket.ticketStatus] || 'hs-status-open'}`}>
              {ticket.ticketStatus}
            </span>
            <span className="hs-detail-ticket-id">Ticket ID: <span>#{ticket.ticketId}</span></span>
          </div>

          {/* Body */}
          <div className="hs-detail-body">

            {/* Left panel */}
            <div className="hs-detail-left">

              {/* Created + Priority */}
              <div className="hs-detail-info-card">
                <div className="hs-detail-info-row">
                  <div className="hs-detail-info-item">
                    <Clock size={16} color='#99A1AF'/>
                    <label>Created:</label>
                    <span>{formatDate(ticket.ticketCreatedAt)}</span>
                  </div>
                  <div className="hs-detail-info-item">
                    <Info size={16} color='#99A1AF'/>
                    <label>Priority:</label>
                    <span style={{ color: priorityColor[ticket.ticketPriority] || 'inherit' }}>
                      {ticket.ticketPriority}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="hs-detail-desc-card">
                <p className="hs-detail-desc-label">Issue Description</p>
                <p className="hs-detail-desc-text">
                  {ticket.ticketDescription || 'No description provided.'}
                </p>
                <div className="hs-detail-notice">
                  <strong>Need immediate assistance?</strong> Our support team typically responds within 2–3 hours during business days. For urgent issues, please call us at <strong>+971 2 000 0000</strong>.
                </div>
              </div>

            </div>

            {/* Right panel — Conversation */}
            <div className="hs-detail-right">
              <p className="hs-conv-label">Conversation</p>

              <div className="hs-conv-list scrollbar">
                {messages.length === 0 ? (
                  <p style={{ color: 'var(--color-sub-title-text)', fontSize: '0.875rem' }}>No messages yet.</p>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className={`hs-msg-row ${msg.sender}`}>
                      {msg.sender === 'self' && (
                        <div className="hs-msg-sender">
                          <span>{msg.senderName}</span>
                          <div className="hs-msg-avatar">{msg.agentInitials}</div>
                        </div>
                      )}
                      {msg.sender === 'agent' && (
                        <div className="hs-msg-sender">
                          <div className="hs-msg-avatar">{msg.agentInitials}</div>
                          <span>{msg.senderName}</span>
                          <span className="hs-msg-agent-badge">Support Team</span>
                        </div>
                      )}
                      <div className="hs-msg-bubble">{msg.text}</div>
                      <span className="hs-msg-time">{msg.time}</span>
                    </div>
                  ))
                )}
                {isTyping && (
                  <div className="hs-msg-row agent">
                    <div className="hs-msg-sender">
                      <div className="hs-msg-avatar">SE</div>
                      <span>SmartEco Bot</span>
                      <span className="hs-msg-agent-badge">Support Team</span>
                    </div>
                    <div className="hs-msg-bubble hs-typing-indicator">
                      <span /><span /><span />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="hs-conv-input-wrap">
                <textarea
                  className="hs-conv-textarea"
                  placeholder="Type your message..."
                  rows={3}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                />
                <div className="hs-conv-input-footer">
                  <button className="hs-attach-btn">
                    <Paperclip size={15} /> Attach File
                  </button>
                  <button className="alerts-add-button" onClick={handleSend}>
                    Send Message
                  </button>
                </div>
              </div>

            </div>
          </div>
         </div>
      </div>
    </DashboardLayout>
  );
};

export default TicketDetailPage;
